import React from 'react';
import { buildPageRange } from '../lib/pagination';

/**
 * Shared AIM table pagination footer — a "N rows" count plus Previous /
 * numbered / Next controls. Pages are 0-indexed. Renders nothing when the data
 * fits on a single page. Mirrors the control already used by the Position
 * Volatility table so every AIM table paginates identically.
 *
 * @param {object} props
 * @param {number} props.page       Current 0-indexed page.
 * @param {number} props.pageCount  Total pages.
 * @param {(next:number)=>void} props.onPage  Called with the next 0-indexed page.
 * @param {number} props.totalItems Total row count (for the label).
 * @param {string} [props.label='row'] Singular noun for the count label.
 */
export default function AimPagination({ page, pageCount, onPage, totalItems, label = 'row' }) {
  if (pageCount <= 1) return null;
  const btn = 'px-2 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800/60 max-[499px]:px-3';
  return (
    <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-600 dark:text-gray-400 max-[499px]:flex-col max-[499px]:items-stretch max-[499px]:gap-3">
      <span>{totalItems} {label}{totalItems === 1 ? '' : 's'}</span>
      <nav aria-label="Table pagination" className="flex items-center gap-1 max-[499px]:w-full max-[499px]:justify-between">
        <button type="button" onClick={() => onPage(Math.max(0, page - 1))} disabled={page === 0} className={btn}>
          Previous
        </button>
        {buildPageRange(page, pageCount).map((p, i) => (
          p === '…' ? (
            <span key={`ellipsis-${i}`} className="px-1 text-gray-400 max-[499px]:hidden" aria-hidden="true">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPage(p)}
              aria-current={p === page ? 'page' : undefined}
              className={`min-w-[28px] px-2 py-1 rounded border text-xs max-[499px]:hidden ${
                p === page
                  ? 'border-[#32B1FF] bg-[#32B1FF] text-white'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60'
              }`}
            >
              {p + 1}
            </button>
          )
        ))}
        <button type="button" onClick={() => onPage(Math.min(pageCount - 1, page + 1))} disabled={page >= pageCount - 1} className={btn}>
          Next
        </button>
      </nav>
    </div>
  );
}
